// Type / syntax / profile detection (D-030, implements D-010 + D-022).
//
// The product must NEVER silently validate a file against rules it does not claim to meet.
// KoSIT's own scenario matcher rejects files whose declared profile version is not configured
// (D-022: real corpus PDFs declaring xrechnung_2.1 get <noScenarioMatched>). We mirror that
// honestly: only XRechnung 3.0.x (the version our pinned config references) is "refereeable"
// and gets a valid/invalid verdict. Everything else is rendered + flagged, not mis-judged.
//
// No multi-version support in v1 — we detect and inform, we do not carry old rule sets.

import type { Detection, DocType, ProfileClass, Syntax } from './types';

// Match the CII root ELEMENT (optionally namespace-prefixed), NOT the bare word anywhere in the
// document — a UBL invoice can legitimately contain the text "CrossIndustryInvoice" in a note or
// line description, which must not flip it to CII.
const CII_ROOT = /<(?:[\w.-]+:)?CrossIndustryInvoice[\s/>]/;

/** UBL (Invoice/CreditNote) vs UN/CEFACT CII (CrossIndustryInvoice). */
export function detectSyntax(xml: string): Syntax {
  return CII_ROOT.test(xml) ? 'CII' : 'UBL';
}

export function detectDocType(xml: string): DocType {
  if (CII_ROOT.test(xml)) return 'CrossIndustryInvoice';
  // UBL root element — CreditNote is a distinct document type from Invoice.
  if (/<(?:[\w.-]+:)?CreditNote[\s/>]/.test(xml)) return 'CreditNote';
  if (/<(?:[\w.-]+:)?Invoice[\s/>]/.test(xml)) return 'Invoice';
  return 'unknown';
}

/** First byte-signature sniff: PDF containers start with "%PDF-". */
export function looksLikePdf(bytes: Uint8Array): boolean {
  return bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;
}

/**
 * Pull the raw profile identifier that carries the CIUS/guideline URN:
 *  - UBL: <cbc:CustomizationID>
 *  - CII: <ram:GuidelineSpecifiedDocumentContextParameter><ram:ID>
 */
export function extractProfileId(xml: string, syntax: Syntax): string | null {
  if (syntax === 'UBL') {
    const m = xml.match(/<cbc:CustomizationID>([\s\S]*?)<\/cbc:CustomizationID>/);
    return m ? m[1].trim() : null;
  }
  const block = xml.match(
    /<ram:GuidelineSpecifiedDocumentContextParameter>[\s\S]*?<ram:ID>([\s\S]*?)<\/ram:ID>[\s\S]*?<\/ram:GuidelineSpecifiedDocumentContextParameter>/,
  );
  return block ? block[1].trim() : null;
}

// The EXACT XRechnung CustomizationID / GuidelineID values our pinned KoSIT config (D-002) has
// scenarios for — matched by EXACT string equality in scenarios.xml (base, extension, cvd; lines
// 47/107/180/…, identical across UBL Invoice, UBL CreditNote and CII). refereeable ⇔ the file
// declares exactly one of these. Anything else — an older 2.x/1.x URN, a FUTURE 3.x, or a 3.0 URN
// with an unrecognized trailing suffix — is one KoSIT returns <noScenarioMatched> for, so we must
// NOT emit an authoritative verdict against the wrong benchmark (D-010/D-022). A substring test
// would falsely referee all of those. Re-derive on every artefact bump (UPDATING.md profile step).
const XRECHNUNG_REFEREEABLE = new Set([
  'urn:cen.eu:en16931:2017#compliant#urn:xeinkauf.de:kosit:xrechnung_3.0',
  'urn:cen.eu:en16931:2017#compliant#urn:xeinkauf.de:kosit:xrechnung_3.0#conformant#urn:xeinkauf.de:kosit:extension:xrechnung_3.0',
  'urn:cen.eu:en16931:2017#compliant#urn:xeinkauf.de:kosit:xrechnung_3.0#compliant#urn:xeinkauf.de:kosit:xrechnung:cvd_0.9',
]);

/**
 * Classify the profile against our pinned referee. `xrechnung-current` (the only refereeable
 * class) is an EXACT match against the config's scenario URNs, mirroring KoSIT's own exact-string
 * scenario matcher; the oracle remains the ultimate referee for the files we DO judge (all
 * refereeable files are diffed against it in CI).
 */
export function classifyProfile(profileId: string | null): ProfileClass {
  if (!profileId) return 'unknown';
  const id = profileId.trim();

  // The ONLY refereeable class: exactly one of the pinned config's XRechnung 3.0 scenario URNs.
  if (XRECHNUNG_REFEREEABLE.has(id)) return 'xrechnung-current';

  const lower = id.toLowerCase();
  // Any OTHER XRechnung URN — an older version (2.x/1.x, or the pre-3.0
  // `xoev-de:kosit:standard:xrechnung` scheme) OR a version/variant our pinned config does not
  // cover (e.g. a future 3.x, a custom-suffixed extension). Either way NOT refereeable.
  if (lower.includes('xrechnung')) return 'xrechnung-older';

  // Non-XRechnung EN 16931 CIUS/extension (e.g. PEPPOL BIS, plain EN16931) — EN16931-compliant
  // but not refereeable by our XRechnung config (D-010).
  if (lower.includes('en16931') || lower.includes('peppol')) return 'en16931-other';

  // Bare ZUGFeRD/Factur-X profiles that do not claim EN 16931 conformance (D-010).
  if (/(minimum|basicwl|basic-wl|:basic|:extended|ferd|factur-x)/.test(lower)) return 'non-en16931';

  return 'unknown';
}

/** Full detection for an already-extracted XML string. */
export function detect(xml: string, container: 'xml' | 'pdf', attachmentName?: string): Detection {
  const syntax = detectSyntax(xml);
  const profileId = extractProfileId(xml, syntax);
  const profileClass = classifyProfile(profileId);
  return {
    container,
    syntax,
    docType: detectDocType(xml),
    profileId,
    profileClass,
    refereeable: profileClass === 'xrechnung-current',
    ...(attachmentName ? { attachmentName } : {}),
  };
}
